import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Box, Grid, Button, Alert, Stack, Typography, LinearProgress, Divider } from '@mui/material';
import { Link } from 'react-router-dom';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  ConfirmationNumber as TicketIcon,
  Schedule as ClockIcon,
  Speed as SpeedIcon,
  Groups as PeopleIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

import { useData } from '../context/DataContext';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Panel from '../components/ui/Panel';
import FilterBar from '../components/ui/FilterBar';
import { barOptions, lineOptions, doughnutOptions, categoryDatasetStyle } from '../theme/charts';
import { categoryPalette, layout } from '../theme/tokens';

const round = (n) => Math.round((Number(n) || 0) * 10) / 10;

/** Formats a `YYYY-MM` or `YYYY-MM-DD` bucket into a short readable label. */
const formatBucket = (value) => {
  if (typeof value !== 'string') return value;
  const parts = value.split('-').map(Number);
  if (parts.length < 2 || parts.some(Number.isNaN)) return value;

  // Daily buckets keep the day; monthly buckets collapse to month + year.
  const isDaily = parts.length >= 3;
  const date = new Date(parts[0], parts[1] - 1, isDaily ? parts[2] : 1);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(
    undefined,
    isDaily ? { day: 'numeric', month: 'short' } : { month: 'short', year: '2-digit' }
  );
};

const Dashboard = () => {
  const {
    summaryData,
    categoryData,
    assigneeData,
    timeSeriesData,
    filterOptions,
    setFilterOptions,
    fetchAnalytics,
    clearFilters,
    loading,
    error,
  } = useData();

  const [dbStatus, setDbStatus] = useState({ available: true, status: 'unknown' });

  useEffect(() => {
    let cancelled = false;
    api
      .get('/health')
      .then((res) => {
        if (!cancelled) {
          setDbStatus({
            available: res.data?.database?.status === 'connected',
            status: res.data?.database?.status || 'unknown',
          });
        }
      })
      .catch(() => {
        if (!cancelled) setDbStatus({ available: false, status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const summary = useMemo(
    () => ({
      totalTickets: summaryData?.totalTickets || 0,
      totalEffort: round(summaryData?.totalEffort),
      avgEffortPerTicket: round(summaryData?.avgEffortPerTicket),
      uniqueAssignees: summaryData?.uniqueAssignees || 0,
    }),
    [summaryData]
  );

  const categories = useMemo(
    () => (categoryData || []).map((c) => ({ name: c._id || 'Other', count: c.count || 0, effort: round(c.totalEffort) })),
    [categoryData]
  );

  /** Effort split by work category. */
  const effortByCategory = useMemo(() => {
    const names = categories.map((c) => c.name);
    return {
      labels: names,
      datasets: [{ label: 'Hours', data: categories.map((c) => c.effort), ...categoryDatasetStyle(names) }],
    };
  }, [categories]);

  /** Ticket volume by category. */
  const volumeByCategory = useMemo(() => {
    const names = categories.map((c) => c.name);
    return {
      labels: names,
      datasets: [{ label: 'Tickets', data: categories.map((c) => c.count), ...categoryDatasetStyle(names) }],
    };
  }, [categories]);

  /** Stacked effort trend, one series per category. */
  const trend = useMemo(() => {
    const rows = timeSeriesData || [];
    if (!rows.length) return { labels: [], datasets: [] };

    const buckets = [...new Set(rows.map((r) => r.date))].sort();
    const names = [...new Set(rows.map((r) => r.category || 'Other'))].sort();
    const index = new Map(rows.map((r) => [`${r.date}|${r.category || 'Other'}`, r.effort || 0]));

    return {
      labels: buckets.map(formatBucket),
      datasets: names.map((name) => {
        const token = categoryPalette[name] || categoryPalette.Other;
        return {
          label: name,
          data: buckets.map((b) => round(index.get(`${b}|${name}`) || 0)),
          backgroundColor: token.soft,
          borderColor: token.main,
          fill: true,
        };
      }),
    };
  }, [timeSeriesData]);

  const topAssignees = useMemo(
    () =>
      [...(assigneeData || [])]
        // Rank by ticket count, matching the server. estimatedEffort is 0 on
        // every row and worklogs cover only 25%, so an hours ranking ranked
        // who files worklogs, not who does work.
        .sort((a, b) => (b.count || 0) - (a.count || 0))
        .slice(0, 6)
        .map((a) => ({ name: a._id, effort: round(a.totalEffort), count: a.count || 0 })),
    [assigneeData]
  );

  const assigneeChart = useMemo(
    () => ({
      labels: topAssignees.map((a) => a.name),
      datasets: [
        {
          label: 'Hours',
          data: topAssignees.map((a) => a.effort),
          backgroundColor: 'rgba(99, 102, 241, 0.75)',
          borderRadius: 6,
          borderWidth: 0,
        },
      ],
    }),
    [topAssignees]
  );

  const handleApply = useCallback((next) => fetchAnalytics(next), [fetchAnalytics]);
  const hasCategories = categories.length > 0;

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Where engineering effort is going across your Jira projects."
        actions={
          <Button component={Link} to="/analytics" variant="outlined" endIcon={<ArrowIcon />}>
            Detailed analytics
          </Button>
        }
      />

      {!dbStatus.available && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Database is not connected ({dbStatus.status}). Figures below may be incomplete.
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <FilterBar
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          onApply={handleApply}
          onClear={clearFilters}
          loading={loading}
        />
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => fetchAnalytics()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Total tickets"
            value={summary.totalTickets.toLocaleString()}
            caption="in selected period"
            icon={<TicketIcon />}
            color="primary.main"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Logged hours"
            value={`${summary.totalEffort.toLocaleString()}h`}
            caption="worklogs only (~25% of tickets)"
            icon={<ClockIcon />}
            color="secondary.main"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Avg per ticket"
            value={`${summary.avgEffortPerTicket}h`}
            caption="mean effort"
            icon={<SpeedIcon />}
            color="warning.main"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            label="Contributors"
            value={summary.uniqueAssignees}
            caption="active assignees"
            icon={<PeopleIcon />}
            color="info.main"
            loading={loading}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Panel
            title="Effort by category"
            subtitle="Share of hours per work type"
            loading={loading}
            isEmpty={!hasCategories}
            emptyMessage="No category data for this period"
            bodyHeight={layout.chartHeight}
          >
            <Doughnut data={effortByCategory} options={doughnutOptions()} />
          </Panel>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Panel
            title="Ticket volume by category"
            subtitle="Number of tickets per work type"
            loading={loading}
            isEmpty={!hasCategories}
            emptyMessage="No category data for this period"
            bodyHeight={layout.chartHeight}
          >
            <Bar data={volumeByCategory} options={barOptions({ yTitle: 'Tickets' })} />
          </Panel>
        </Grid>

        <Grid size={{ xs: 12, lg: 8 }}>
          <Panel
            title="Effort trend"
            subtitle="Hours per category over time"
            loading={loading}
            isEmpty={!trend.labels.length}
            emptyMessage="No trend data for this period"
            bodyHeight={layout.chartHeight}
          >
            <Line data={trend} options={lineOptions({ yTitle: 'Hours', stacked: true })} />
          </Panel>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Panel
            title="Top contributors"
            subtitle="By hours in period"
            loading={loading}
            isEmpty={!topAssignees.length}
            emptyMessage="No assignee data"
            bodyHeight={layout.chartHeight}
          >
            <Bar
              data={assigneeChart}
              options={barOptions({ horizontal: true, yTitle: undefined })}
            />
          </Panel>
        </Grid>

        <Grid size={12}>
          <Panel title="Category breakdown" loading={loading} isEmpty={!hasCategories}>
            <Stack divider={<Divider flexItem />} spacing={0}>
              {categories.map((c) => {
                const token = categoryPalette[c.name] || categoryPalette.Other;
                const pct = summary.totalEffort ? (c.effort / summary.totalEffort) * 100 : 0;
                return (
                  <Stack
                    key={c.name}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ py: 1.5 }}
                  >
                    <Box
                      aria-hidden
                      sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: token.main, flexShrink: 0 }}
                    />
                    <Typography variant="body2" sx={{ width: 110, fontWeight: 600 }}>
                      {c.name}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 80 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(pct, 100)}
                        aria-label={`${c.name} share of total effort`}
                        sx={{
                          bgcolor: 'rgba(148,163,184,0.12)',
                          '& .MuiLinearProgress-bar': { backgroundColor: token.main },
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ width: 68, textAlign: 'right' }}>
                      {c.count} tix
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ width: 84, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
                    >
                      {c.effort}h
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 48, textAlign: 'right' }}>
                      {Math.round(pct)}%
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
